import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppConsts } from '@shared/AppConsts';

/**
 * Hand-written proxy for the PiiReviewAppService ABP dynamic API (same approach as
 * TtwTrackingSheetService, so this ships without a service-proxies regeneration round-trip).
 * Errors are shown by the global ZeroTemplateHttpInterceptor.
 */

export enum PiiReviewChannel {
  Email = 1,
  Sms = 2,
}

export enum PiiReviewStatus {
  Blocked = 0,
  PendingReview = 1,
  Approved = 2,
  Rejected = 3,
}

/** What happened to an approved message when it was sent on approval (mirrors Thrive.TTW.Pii.PiiSendStatus). */
export enum PiiSendStatus {
  NotSent = 0,
  Sent = 1,
  Failed = 2,
  NotAutoSendable = 3,
  Sending = 4,
}

export interface PiiSubmitResultDto {
  id: number;
  piiNumber: string;
}

/** A held message as shown in the SMS panel: content-free (reference, status, when). */
export interface PiiHeldMessageDto {
  id: number;
  piiNumber: string;
  channel: PiiReviewChannel;
  status: PiiReviewStatus;
  statusName: string;
  sendStatus: PiiSendStatus;
  recipient?: string;
  creationTime: string;
}

export interface PiiReviewQueueItemDto {
  id: number;
  /** The reference shown everywhere a held message appears, e.g. "PII-000042". */
  piiNumber: string;
  channel: PiiReviewChannel;
  channelName: string;
  status: PiiReviewStatus;
  statusName: string;
  recipient?: string;
  ticketHolderId?: number;
  ticketHolderName?: string;
  formattedTicketHolderId?: string;
  detectedPii?: string;
  requestedByName?: string;
  creationTime: string;
  submittedForReviewTime?: string;
  reviewedByName?: string;
  reviewedTime?: string;
  reviewNotes?: string;
  /** Approving sends the message; this is how that went. */
  sendStatus: PiiSendStatus;
  sendStatusName?: string;
  sentTime?: string;
  /** Why the send failed -- a system message, never message content. */
  sendError?: string;
  /** True when "Retry send" makes sense (the send failed, or got stuck). */
  canRetrySend: boolean;
}

export interface PiiReviewAttachmentDto {
  fileName: string;
  contentType?: string;
  size: number;
}

export interface PiiReviewDetailDto extends PiiReviewQueueItemDto {
  subject?: string;
  body?: string;
  attachments: PiiReviewAttachmentDto[];
}

export interface PagedResult<T> {
  totalCount: number;
  items: T[];
}

export interface GetPiiReviewQueueInput {
  status?: PiiReviewStatus | null;
  filter?: string;
  sorting?: string;
  skipCount: number;
  maxResultCount: number;
}

interface AbpResult<T> {
  result: T;
}

@Injectable({ providedIn: 'root' })
export class PiiReviewService {
  private readonly baseUrl = AppConsts.remoteServiceBaseUrl + '/api/services/app/PiiReview';

  constructor(private http: HttpClient) {}

  submitForReview(id: number): Observable<PiiSubmitResultDto> {
    return this.http.post<AbpResult<PiiSubmitResultDto>>(`${this.baseUrl}/SubmitForReview`, { id }).pipe(map((r) => r.result));
  }

  /** The sender's own view of a held message (or a reviewer's) -- what the "view detail" link in a notification opens. */
  getMyDetail(id: number): Observable<PiiReviewDetailDto> {
    return this.http
      .get<AbpResult<PiiReviewDetailDto>>(`${this.baseUrl}/GetMyDetail`, { params: new HttpParams().set('Id', id) })
      .pipe(map((r) => r.result));
  }

  /** Content-free list of the ticket holder's messages held for PII review (pending / approved / rejected). */
  getHeldByTicketHolder(ticketHolderId: number, channel?: PiiReviewChannel): Observable<PiiHeldMessageDto[]> {
    let params = new HttpParams().set('ticketHolderId', ticketHolderId);
    if (channel !== undefined) {
      params = params.set('channel', channel);
    }
    return this.http
      .get<AbpResult<PiiHeldMessageDto[]>>(`${this.baseUrl}/GetHeldByTicketHolder`, { params })
      .pipe(map((r) => r.result ?? []));
  }

  getQueue(input: GetPiiReviewQueueInput): Observable<PagedResult<PiiReviewQueueItemDto>> {
    let params = new HttpParams().set('SkipCount', input.skipCount).set('MaxResultCount', input.maxResultCount);
    if (input.status !== null && input.status !== undefined) {
      params = params.set('Status', input.status);
    }
    if (input.filter) {
      params = params.set('Filter', input.filter);
    }
    if (input.sorting) {
      params = params.set('Sorting', input.sorting);
    }
    return this.http
      .get<AbpResult<PagedResult<PiiReviewQueueItemDto>>>(`${this.baseUrl}/GetQueue`, { params })
      .pipe(map(r => r.result));
  }

  getDetail(id: number): Observable<PiiReviewDetailDto> {
    return this.http
      .get<AbpResult<PiiReviewDetailDto>>(`${this.baseUrl}/GetDetail`, { params: new HttpParams().set('Id', id) })
      .pipe(map(r => r.result));
  }

  /** Approving also sends the message, as its original sender. */
  review(id: number, approve: boolean, notes?: string): Observable<void> {
    return this.http.post<AbpResult<void>>(`${this.baseUrl}/Review`, { id, approve, notes }).pipe(map(() => undefined));
  }

  /** Tries again to send an approved message whose send failed. */
  retrySend(id: number): Observable<void> {
    return this.http.post<AbpResult<void>>(`${this.baseUrl}/RetrySend`, { id }).pipe(map(() => undefined));
  }
}
